import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle } from 'lucide-react';
import clsx from 'clsx';

export interface BookingStepperStep {
  key: string;
  label: string;
  icon?: React.ReactNode;
}

interface BookingStepperProps {
  steps: BookingStepperStep[];
  /** 0-based index of the current step */
  currentIndex: number;
  /**
   * `numbered` — piercing-style circles with step numbers
   * `icons` — tattoo-style circles with icons / checkmarks
   */
  variant?: 'numbered' | 'icons';
  className?: string;
}

/**
 * Responsive booking progress:
 * - mobile: compact "Step X of N" + progress bar + current label
 * - md+: compact horizontal timeline (smaller icons to save vertical space)
 */
export function BookingStepper({
  steps,
  currentIndex,
  variant = 'numbered',
  className,
}: BookingStepperProps) {
  const { t } = useTranslation();
  const safeIndex = Math.max(0, Math.min(currentIndex, steps.length - 1));
  const current = steps[safeIndex];
  const progressPct =
    steps.length <= 1 ? 100 : Math.round((safeIndex / (steps.length - 1)) * 100);

  return (
    <div className={clsx('mb-3 md:mb-4', className)}>
      {/* Mobile compact */}
      <div className="md:hidden" aria-label={t('bookingUi.stepOf', { current: safeIndex + 1, total: steps.length })}>
        <div className="flex items-baseline justify-between gap-3 mb-1.5">
          <p className="text-[11px] uppercase tracking-wider text-textSecondary">
            {t('bookingUi.stepOf', { current: safeIndex + 1, total: steps.length })}
          </p>
          <p className="text-sm font-medium text-textPrimary text-right truncate">
            {current?.label}
          </p>
        </div>
        <div className="h-1 w-full rounded-full bg-gray-200 overflow-hidden">
          <div
            className="h-full rounded-full bg-primary-300 transition-all duration-300"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <div className="mt-2 flex items-center justify-center gap-1" aria-hidden>
          {steps.map((step, index) => (
            <span
              key={step.key}
              className={clsx(
                'h-1 rounded-full transition-all',
                index === safeIndex
                  ? 'w-3.5 bg-primary-300'
                  : index < safeIndex
                    ? 'w-1 bg-primary-300/70'
                    : 'w-1 bg-gray-300'
              )}
            />
          ))}
        </div>
      </div>

      {/* Desktop / tablet full timeline */}
      {variant === 'numbered' ? (
        <div className="hidden md:flex items-center justify-center space-x-2">
          {steps.map((step, index, array) => {
            const stepNumber = index + 1;
            const isActive = safeIndex >= index;
            return (
              <React.Fragment key={step.key}>
                <div
                  className={clsx(
                    'flex flex-col items-center text-center flex-shrink-0',
                    isActive ? 'text-textPrimary' : 'text-textSecondary'
                  )}
                >
                  <div
                    className={clsx(
                      'w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium',
                      isActive ? 'bg-primary-300 text-white' : 'bg-gray-200 text-textSecondary'
                    )}
                  >
                    {stepNumber}
                  </div>
                  <p className="mt-1 text-[11px] font-medium leading-tight max-w-[5.5rem]">
                    {step.label}
                  </p>
                </div>
                {index < array.length - 1 && (
                  <div className="flex-1 h-px bg-gray-300 mx-1.5 min-w-[1rem]" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      ) : (
        <div className="hidden md:flex items-start justify-between gap-1 w-full">
          {steps.map((step, index) => (
            <React.Fragment key={step.key}>
              <div className="flex flex-col items-center text-center flex-1 min-w-0 px-0.5">
                <div
                  className={clsx(
                    'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 [&>svg]:w-4 [&>svg]:h-4',
                    index < safeIndex
                      ? 'bg-green-500 text-white'
                      : index === safeIndex
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-500'
                  )}
                >
                  {index < safeIndex ? <CheckCircle className="w-4 h-4" /> : step.icon}
                </div>
                <span
                  className={clsx(
                    'mt-1 text-[11px] font-medium leading-tight text-center break-words max-w-[4.75rem]',
                    index < safeIndex
                      ? 'text-green-600'
                      : index === safeIndex
                        ? 'text-blue-600'
                        : 'text-gray-500'
                  )}
                >
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={clsx(
                    'mt-4 h-0.5 flex-1 min-w-[0.5rem] max-w-[2rem] rounded-full',
                    index < safeIndex ? 'bg-green-500' : 'bg-gray-200'
                  )}
                  aria-hidden
                />
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
